import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const KEY = "skandasri-booking-list";
const RECENT_KEY = "skandasri-recently-viewed";
const RECENT_DAY = "skandasri-recently-viewed-day";
const today = () => new Date().toDateString();
const loadRecent = () => (localStorage.getItem(RECENT_DAY) === today() ? load(RECENT_KEY) : []);
const CartCtx = createContext(null);
export const useCart = () => useContext(CartCtx);

const load = (key = KEY) => {
  try {
    const v = JSON.parse(localStorage.getItem(key) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => load());
  const [recent, setRecent] = useState(() => loadRecent());

  // Recently viewed is cleared at the end of each day
  useEffect(() => {
    localStorage.setItem(RECENT_KEY, JSON.stringify(recent));
    localStorage.setItem(RECENT_DAY, today());
  }, [recent]);

  const addRecent = useCallback(
    (item) => setRecent((cur) => [item, ...(localStorage.getItem(RECENT_DAY) === today() ? cur : []).filter((i) => i.id !== item.id)].slice(0, 12)),
    [],
  );

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  // keep several open tabs in sync
  useEffect(() => {
    const sync = (e) => { if (e.key === KEY) setItems(load()); };
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  const has = useCallback((id) => items.some((i) => i.id === id), [items]);
  const add = useCallback((item) => setItems((cur) => (cur.some((i) => i.id === item.id) ? cur : [...cur, { ...item, addedAt: Date.now() }])), []);
  const remove = useCallback((id) => setItems((cur) => cur.filter((i) => i.id !== id)), []);
  const update = useCallback((id, patch) => setItems((cur) => cur.map((i) => (i.id === id ? { ...i, ...patch } : i))), []);
  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(() => ({ items, has, add, remove, update, clear, recent, addRecent }), [items, has, add, remove, update, clear, recent, addRecent]);
  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
};
